import React, { useState, useEffect } from 'react';
import { IoChevronDownSharp } from 'react-icons/io5';
import { FaChevronDown } from 'react-icons/fa';

interface AccordionItemProps {
  id: string;
  suggestion: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
  onApprove: (suggestion: string, id: string) => void;
  onDismiss: (id: string) => void;
  loading?: boolean;
}

// Generate fake ATS scores for now
const generateFakeScore = (text: string): number => {
  // Use text length to generate consistent fake scores between 2-6
  const hash = text.length + text.charCodeAt(0);
  return Math.floor((hash % 5) + 2); // Scores between 2-6
};

// Truncate text for closed state preview
const truncateText = (text: string, maxLength: number = 45): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
};

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  suggestion,
  isOpen,
  onToggle,
  onApprove,
  onDismiss,
  loading = false
}) => {
  const [showPreview, setShowPreview] = useState(!isOpen);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingAction, setPendingAction] = useState<'approve' | 'dismiss' | null>(null);

  // Handle smooth preview text transitions
  useEffect(() => {
    if (isOpen) {
      // Hide preview immediately when opening
      setShowPreview(false);
    } else {
      // Show preview with delay when closing to sync with animation
      const timer = setTimeout(() => {
        setShowPreview(true);
      }, 50); // Small delay to let accordion start closing
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (isRemoving) return; // Prevent toggle during removal animation
    onToggle(id);
  };

  const atsScore = generateFakeScore(suggestion);
  const truncatedText = truncateText(suggestion);

  const triggerRemoval = (action: 'approve' | 'dismiss') => {
    setIsRemoving(true);
    setPendingAction(action);
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (isRemoving && e.propertyName === 'max-height') {
      if (pendingAction === 'approve') {
        onApprove(suggestion, id);
      } else if (pendingAction === 'dismiss') {
        onDismiss(id);
      }
    }
  };

  return (
    <div 
      className={`accordion-item ${isOpen ? 'open' : 'closed'} ${isRemoving ? 'collapsing' : ''}`} 
      data-accordion-id={id}
      onClick={handleToggle}
      onTransitionEnd={handleTransitionEnd}
      style={{ cursor: 'pointer' }}
    >
      <div className="accordion-trigger">
        <div className="suggestion-content">
          {/* Always show ATS Score */}
          <div className="ats-score-section">
            <span className="ats-score">+{atsScore} ATS Points</span>
          </div>
          
          {/* Closed State: Just preview text */}
          <div className={`closed-preview ${showPreview ? 'visible' : 'hidden'}`}>
            <span className="separator">•</span>
            <span className="preview-text">{truncatedText}</span>
          </div>
        </div>
        
        <IoChevronDownSharp 
          className="dropdown-icon"
          size={18}
        />
      </div>
      
      {/* Open State: Full text + buttons */}
      <div className="accordion-content">
        <div className="accordion-content-inner">
          <div className="full-suggestion-text">
            {suggestion}
          </div>
          <div className="button-container">
            <button
              className="suggestion-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                triggerRemoval('approve');
              }}
              disabled={loading}
              style={{ cursor: 'pointer' }}
            >
              Approve
            </button>
            <button
              className="suggestion-secondary-btn"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                triggerRemoval('dismiss');
              }}
              style={{ cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CustomAccordionProps {
  items: Array<{
    id: string;
    suggestion: string;
  }>;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onApprove: (suggestion: string, id: string) => void;
  onDismiss: (id: string) => void;
  loading?: boolean;
}

const CustomAccordion: React.FC<CustomAccordionProps> = ({
  items,
  expandedId,
  onToggle,
  onApprove,
  onDismiss,
  loading = false
}) => {
  const handleToggle = (id: string) => {
    // If clicking the same item that's open, close it
    if (expandedId === id) {
      onToggle('');
    } else {
      onToggle(id);
    }
  };

  return (
    <div className="AccordionRoot">
      {items && items.length > 0 ? (
        items.map((item) => (
          <AccordionItem
            key={item.id}
            id={item.id}
            suggestion={item.suggestion}
            isOpen={expandedId === item.id}
            onToggle={handleToggle}
            onApprove={onApprove}
            onDismiss={onDismiss}
            loading={loading}
          />
        ))
      ) : (
        // Placeholder items to maintain layout
        Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="accordion-item placeholder">
            <div className="accordion-trigger">
              <div className="placeholder-text no-suggestions" />
              <FaChevronDown className="dropdown-icon" />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CustomAccordion; 